import { Button, Toggle } from '@warp-ds/react';
import cn from 'classnames';
import { useEffect, useRef } from 'react';
import type { PrepackageDestination } from '../../types';
import type { SearchIndexItem } from '../utils/destinationPicker';
import { nodeHasSelectedDescendant, nodeToDestination } from '../utils/destinationPicker';
import { destinationTypeLabels } from './labels';

type DestinationPickerSearchViewProps = {
    isMobile?: boolean;
    searchQuery: string;
    searchResults: SearchIndexItem[];
    partialMatches: SearchIndexItem[];
    popularCountries: SearchIndexItem[];
    selectedDestinations: PrepackageDestination[];
    isDestinationSelected: (destination: PrepackageDestination) => boolean;
    isMaxedOut: boolean;
    onToggleDestination: (destination: PrepackageDestination) => void;
    onShowBrowse: () => void;
};

const ITEM_TYPE_ICON: Record<SearchIndexItem['type'], string> = {
    continent: '',
    country: 'Globe',
    region: 'PinRound',
    destination: 'PinMarker',
};

export const DestinationPickerSearchView = ({
    isMobile = false,
    searchQuery,
    searchResults,
    partialMatches,
    popularCountries,
    selectedDestinations,
    isDestinationSelected,
    isMaxedOut,
    onToggleDestination,
    onShowBrowse,
}: DestinationPickerSearchViewProps) => {
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        }
    }, [searchQuery]);

    const renderSearchItem = (item: SearchIndexItem) => {
        const destination = nodeToDestination(item.node, item.country);
        const checked = isDestinationSelected(destination);
        const indeterminate = nodeHasSelectedDescendant({
            node: item.node,
            country: item.country,
            selected: selectedDestinations,
        });

        const parentPath = item.path[item.path.length - 1];
        const showParentPath = (item.type === 'region' || item.type === 'destination') && Boolean(parentPath);
        const subtitle = showParentPath ? `${destinationTypeLabels[item.type]} i ${parentPath}` : destinationTypeLabels[item.type];
        const iconName = ITEM_TYPE_ICON[item.type];

        return (
            <li
                key={`${item.type}-${destination.label}`}
                className={cn('border-b s-border last:border-b-0', { 'px-12': !isMobile, 'px-16': isMobile })}>
                <Toggle
                    type="checkbox"
                    label={
                        <span className="flex flex-col w-full gap-4">
                            <span className="block font-bold">{item.node.name}</span>
                            <span className="flex items-center gap-4 text-s s-text-subtle">
                                {iconName && <w-icon name={iconName} size="small" locale="nb" aria-hidden></w-icon>}
                                <span className="pt-2">{subtitle}</span>
                            </span>
                        </span>
                    }
                    checked={checked}
                    indeterminate={indeterminate}
                    disabled={isMaxedOut && !checked}
                    onChange={() => onToggleDestination(destination)}
                    className="py-8"
                />
            </li>
        );
    };

    return (
        <div ref={listRef} className={cn({ 'max-h-[360px] overflow-y-auto': !isMobile })}>
            {searchResults.length > 0 ? (
                <ul className="list-none p-0">{searchResults.map(renderSearchItem)}</ul>
            ) : (
                <div className="p-16">
                    <p className="mb-4 font-bold">{`Fant ingen treff for "${searchQuery}"`}</p>
                    <p className="mb-16 text-s s-text-subtle">Sjekk stavemåten, eller utforsk forslagene under</p>
                    {partialMatches.length > 0 && (
                        <>
                            <p className="mb-8 text-s font-bold">Mente du?</p>
                            <div className="mb-16 flex flex-wrap gap-8">
                                {partialMatches.map((match) => (
                                    <Button
                                        key={`${match.type}-${match.node.displayName}`}
                                        type="button"
                                        utility
                                        small
                                        onClick={() => onToggleDestination(nodeToDestination(match.node, match.country))}>
                                        {match.node.name}
                                    </Button>
                                ))}
                            </div>
                        </>
                    )}
                    <p className="mb-8 text-s font-bold">Populære reisemål</p>
                    <div className="mb-16 flex flex-wrap gap-8">
                        {popularCountries.map((country) => (
                            <Button
                                key={country.node.displayName}
                                type="button"
                                utility
                                small
                                onClick={() => onToggleDestination(nodeToDestination(country.node, country.country))}>
                                {country.node.name}
                            </Button>
                        ))}
                    </div>
                    <Button type="button" secondary onClick={onShowBrowse}>
                        Se alle reisemål
                    </Button>
                </div>
            )}
        </div>
    );
};
